import {Controller} from '@hotwired/stimulus';
import $ from 'jquery';
import * as moment from 'moment/moment';
import {useModal} from '../js/stimulus_use/use-modal';

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static targets = [
        'litigantName',
        'recapDate',
        'recapStart',
        'recapEnd',
        'recapMedium',
        'dateInput',
        'startInput',
        'endInput',
        'mediumInput',
        'guide',
        'alert',
        'message',
    ];

    static outlets = [
        'attachment-staging',
    ];

    static values = {
        bookingId: Number,
    };

    slotCheckTimeout = null;

    connect() {
        useModal(this);
    }

    // Drop a debounced slot-check still pending when the modal closes, so it doesn't fire a
    // needless request and write into the now-hidden alert.
    onHide() {
        if (this.slotCheckTimeout) {
            clearTimeout(this.slotCheckTimeout);
            this.slotCheckTimeout = null;
        }
    }

    // Loads the booking into the recap and the editable fields, wires the pickers,
    // then opens. Called by modify-booking-link once bookingIdValue is set on this outlet.
    showContent() {
        showPreloader();

        $.get(routes['app_booking_view'].replace('__id__', this.bookingIdValue), response => {
            this.litigantNameTargets.forEach(element => {
                element.textContent = response.name;
            });

            // Recap: the appointment currently being modified (display-formatted).
            this.recapDateTarget.textContent = response.date_label;
            this.recapStartTarget.textContent = response.start_label;
            this.recapEndTarget.textContent = response.end_label;
            this.recapMediumTarget.textContent = response.medium_label;

            // Editable fields start from the current raw values.
            this.dateInputTarget.value = moment(response.date, 'YYYY-MM-DD').format('DD/MM/YYYY');
            this.startInputTarget.value = response.start;
            this.endInputTarget.value = response.end;
            this.setMedium(response.medium);

            this.updateGuide();
            this.hideAlert();
            this.messageTarget.value = '';

            if (this.hasAttachmentStagingOutlet) {
                this.attachmentStagingOutlet.clear();
            }

            this.setupPickers();

            this.open();
        }).fail(() => {
            addAlert('danger', translations['profile.An unknown error occurred.']);
        }).always(() => {
            hidePreloader();
        });
    }

    // Mirrors the working init in messages.js: build the pickers, then push the current
    // values into them so the modal opens on the booking's own slot.
    setupPickers() {
        const startDate = moment(this.dateInputTarget.value, 'DD/MM/YYYY');

        $(this.dateInputTarget).daterangepicker({
            startDate: startDate,
            minDate: moment(),
            cancelClass: 'btn-light',
            applyButtonClasses: 'btn-success',
            singleDatePicker: true,
            locale: {
                firstDay: 1,
                format: 'DD/MM/YYYY',
                applyLabel: translations['date_picker.apply_label'],
                cancelLabel: translations['date_picker.cancel_label'],
                daysOfWeek: [
                    translations['date_picker.days_of_week.sunday'],
                    translations['date_picker.days_of_week.monday'],
                    translations['date_picker.days_of_week.tuesday'],
                    translations['date_picker.days_of_week.wednesday'],
                    translations['date_picker.days_of_week.thursday'],
                    translations['date_picker.days_of_week.friday'],
                    translations['date_picker.days_of_week.saturday'],
                ],
                monthNames: [
                    translations['date_picker.month_names.january'],
                    translations['date_picker.month_names.february'],
                    translations['date_picker.month_names.march'],
                    translations['date_picker.month_names.april'],
                    translations['date_picker.month_names.may'],
                    translations['date_picker.month_names.june'],
                    translations['date_picker.month_names.july'],
                    translations['date_picker.month_names.august'],
                    translations['date_picker.month_names.september'],
                    translations['date_picker.month_names.october'],
                    translations['date_picker.month_names.november'],
                    translations['date_picker.month_names.december'],
                ],
            },
        });
        $(this.dateInputTarget).data('daterangepicker').setStartDate(startDate);

        [this.startInputTarget, this.endInputTarget].forEach(element => {
            $(element).timepicker({
                minuteStep: 5,
                showSeconds: false,
                showMeridian: false,
                icons: {
                    up: 'mdi mdi-chevron-up',
                    down: 'mdi mdi-chevron-down',
                },
            });
            $(element).data('timepicker').updateFromElementVal();
        });

        // The appended calendar icon and time carets should open their picker, not just the
        // input itself — forward a click on the addon to the field.
        $(this.element).find('.booking-modal__slot-row .input-group-append')
            .off('click.slotAddon')
            .on('click.slotAddon', event => {
                $(event.currentTarget).siblings('input').trigger('click');
            });
    }

    // The medium is an account-standard Tom Select, so its display only follows a programmatic
    // change via the instance's setValue; a bare .value= leaves the control showing the old label.
    setMedium(medium) {
        if (this.mediumInputTarget.tomselect) {
            this.mediumInputTarget.tomselect.setValue(medium, true);
        } else {
            this.mediumInputTarget.value = medium;
        }
    }

    // The medium note ("we'll send the office address...", etc.) tracks the select.
    updateGuide() {
        this.guideTarget.innerHTML = translations['booking_invite.Appointment by ' + this.mediumInputTarget.value] || '';
    }

    // Debounced availability check, same endpoint/behaviour as the old modify flow.
    checkSlot() {
        this.hideAlert();

        if (this.slotCheckTimeout) {
            clearTimeout(this.slotCheckTimeout);
        }

        this.slotCheckTimeout = setTimeout(() => {
            const data = {
                booking_id: this.bookingIdValue,
                date: moment(this.dateInputTarget.value, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                start: moment(this.startInputTarget.value, 'HH:mm').format('HH:mm'),
                end: moment(this.endInputTarget.value, 'HH:mm').format('HH:mm'),
            };

            $.get(routes['app_booking_isslotfree'], data, result => {
                if (result.status) {
                    if (!result.is_slot_free) {
                        $(this.alertTarget)
                            .removeClass('text-danger')
                            .addClass('text-warning')
                            .html(translations['manage_booking.The selected date is already busy. You can still continue but check you agenda!'])
                            .show();
                    }
                } else {
                    $(this.alertTarget)
                        .removeClass('text-warning')
                        .addClass('text-danger')
                        .html(result.message)
                        .show();
                }
            });
        }, 800);
    }

    hideAlert() {
        $(this.alertTarget).html('').hide();
    }

    submit(event) {
        if (event) {
            event.preventDefault();
        }

        showPreloader();

        // Multipart so the staged files ride along with the fields; the fields still land in
        // $request->request and the files under $request->files->all('document') server-side.
        const formData = new FormData();
        formData.append('action', 'modify');
        // The datepicker holds DD/MM/YYYY; app_booking_edit parses Y-m-d.
        formData.append('date', moment(this.dateInputTarget.value, 'DD/MM/YYYY').format('YYYY-MM-DD'));
        formData.append('start', this.startInputTarget.value);
        formData.append('end', this.endInputTarget.value);
        formData.append('medium', this.mediumInputTarget.value);
        formData.append('message', this.messageTarget.value);

        if (this.hasAttachmentStagingOutlet) {
            this.attachmentStagingOutlet.getFiles().forEach(file => {
                formData.append('document[]', file);
            });
        }

        $.ajax({
            type: 'POST',
            url: routes['app_booking_edit'].replace('__id__', this.bookingIdValue),
            data: formData,
            processData: false,
            contentType: false,
        }).done(response => {
            if (response.status) {
                this.close();
                // Rebuilds the chat, which also clears the preloader.
                reloadChatAndUserInformation();
            } else {
                addAlert('danger', response.message || translations['profile.An unknown error occurred.']);

                hidePreloader();
            }
        }).fail(() => {
            addAlert('danger', translations['profile.An unknown error occurred.']);

            hidePreloader();
        });
    }
}
